import { arredondar2 } from '../pedidos/precos'
import { mesmoFabricante, nomeDoAlvoFabricante } from './fabricante'
import type {
  ComissaoPercentual, ItemPedido, LancamentoComissao, Produto, RegraComissao,
} from '../db/tipos'

// Percentual EXPLÍCITO do produto (produto>fabricante>linha>categoria), sem cair no pct_padrao.
// null = não há percentual específico (o produto usaria o fallback da regra).
export function pctExplicitoParaProduto(
  regra: RegraComissao,
  percentuais: ComissaoPercentual[],
  produto: Produto,
): number | null {
  const ativos = percentuais.filter((p) => p.regra_id === regra.id && p.apagado_em === null)
  const porProduto = ativos.find((p) => p.alvo === `produto:${produto.id}`)
  if (porProduto) return porProduto.pct
  // Fabricante entra ENTRE produto e linha -- ordem escolhida por ela em
  // 26/08: produto > fabricante > linha > categoria. Ha guarda de contrato
  // sobre esta ordem: mudar aqui e mudar dinheiro.
  //
  // ⚠ So o fabricante casa NORMALIZADO (caixa/acento/espaco). linha e
  // categoria seguem por texto exato de proposito: normaliza-los alteraria
  // comissao de dado que ja existe.
  if (produto.fabricante) {
    const porFabricante = ativos.find((p) => {
      const nome = nomeDoAlvoFabricante(p.alvo)
      return nome !== null && mesmoFabricante(nome, produto.fabricante)
    })
    if (porFabricante) return porFabricante.pct
  }
  if (produto.linha) {
    const porLinha = ativos.find((p) => p.alvo === `linha:${produto.linha}`)
    if (porLinha) return porLinha.pct
  }
  if (produto.categoria) {
    const porCategoria = ativos.find((p) => p.alvo === `categoria:${produto.categoria}`)
    if (porCategoria) return porCategoria.pct
  }
  return null
}

export function pctParaProduto(
  regra: RegraComissao,
  percentuais: ComissaoPercentual[],
  produto: Produto,
): number | null {
  if (regra.tipo === 'pct_fixo') return regra.pct_padrao
  return pctExplicitoParaProduto(regra, percentuais, produto) ?? regra.pct_padrao
}

// base = valor sobre o qual a comissao incide (valor_total ou valor_faturado).
// No pct_por_item, quando base != valorTotal (corte da fabrica), escala proporcional.
export function calcularComissao(
  regra: RegraComissao,
  percentuais: ComissaoPercentual[],
  itens: Array<{ item: ItemPedido; produto: Produto }>,
  base: number,
  valorTotal: number,
): number {
  if (regra.tipo === 'pct_fixo') {
    return arredondar2((base * (regra.pct_padrao ?? 0)) / 100)
  }
  const fator = valorTotal > 0 ? base / valorTotal : 0
  let total = 0
  for (const entrada of itens) {
    const pct = pctParaProduto(regra, percentuais, entrada.produto) ?? 0
    total += (entrada.item.total_item * fator * pct) / 100
  }
  return arredondar2(total)
}

export function valorEfetivo(lancamento: LancamentoComissao): number {
  return lancamento.valor_recalculado ?? lancamento.valor_previsto
}

// Seleciona a regra da representada conforme a tabela de preco do sub-pedido:
// override da tabela quando existir, senao a regra padrao (tabela_preco_id null).
// QUANDO a fabrica paga e acordo com a FORNECEDORA (palavra dela, 2026-07-30),
// nao muda porque o produto esta numa promocao. Antes, a regra da tabela
// substituia a padrao por inteiro -- e como as regras por tabela nascem com
// 'faturamento' fixo, a escolha dela na fornecedora ficava visivel na tela e
// nao valia para pedido nenhum.
//
// O percentual continua sendo o da tabela; so o gatilho sobe para a
// fornecedora. Sem regra padrao nao ha de quem herdar: vale a da propria regra.
export function gatilhoEfetivo(
  regras: RegraComissao[],
  escolhida: RegraComissao,
): RegraComissao['gatilho_liberacao'] {
  const padrao = regras.find((r) => r.apagado_em === null && r.tabela_preco_id === null)
  return padrao?.gatilho_liberacao ?? escolhida.gatilho_liberacao
}

// As duas regras que existem no modelo, cada uma respondendo uma pergunta
// DIFERENTE de escolherRegra. escolherRegra diz "quem GOVERNA esta tabela"; estas
// dizem "qual e a regra propria dela" e "qual e a padrao da marca" -- que e o que
// a importacao precisa saber para oferecer trocar uma pela outra.
export function regraPropriaDaTabela(
  regras: RegraComissao[],
  tabelaPrecoId: string | undefined,
): RegraComissao | undefined {
  if (!tabelaPrecoId) return undefined
  return regras.find((r) => r.apagado_em === null && r.tabela_preco_id === tabelaPrecoId)
}

export function regraPadraoDaFornecedora(regras: RegraComissao[]): RegraComissao | undefined {
  return regras.find((r) => r.apagado_em === null && r.tabela_preco_id === null)
}

export function escolherRegra(
  regras: RegraComissao[],
  tabelaPrecoId: string | null,
): RegraComissao | undefined {
  const ativas = regras.filter((r) => r.apagado_em === null)
  if (tabelaPrecoId !== null) {
    const override = ativas.find((r) => r.tabela_preco_id === tabelaPrecoId)
    if (override) return override
  }
  return ativas.find((r) => r.tabela_preco_id === null)
}
